import Vue from 'vue';
import api from '../../api/spot';
import Utils from './base.js';
import moment from 'moment-timezone';
import CONFIG from '@/constants/config.js';
import GeoLocation from '@/components/map/GeoLocation.js';
import { replaceCustomKeywords } from '@/utils/custom-keywords.mjs';
import { countdownStartMs, hasCountdown, hasDateValue, nthCheckoutTime } from '@/utils/event-time.mjs';
import { isCantPlayAgainError } from '@/utils/request-error.mjs';
import { canAutoCheckinNow, isAutoCheckinSpot } from '@/utils/auto-checkin.mjs';


const methods = {
    /**
     * Check if is a jsTask
     * @param {*} item
     * @returns
     */
    isSolutionTypeJsTask(item) {
        return item.raetsel && (item.raetsel.ralsgtyp == CONFIG.SOLUTION_TYPE_JS_TASK_ID)
    },
    mapSpot(list) {
        return list && list.data ? list.data : list;
    },
    applyCustomKeywordsToSpot(task, definitions) {
        if (!task) {
            return task;
        }
        const replaceField = (obj, field) => {
            if (obj && typeof obj[field] === 'string') {
                obj[field] = replaceCustomKeywords(obj[field], definitions);
            }
        };
        const replaceListField = (obj, field) => {
            if (obj && Vue._.isArray(obj[field])) {
                obj[field] = obj[field].map((entry) =>
                    typeof entry === 'string' ? replaceCustomKeywords(entry, definitions) : entry
                );
            }
        };
        replaceField(task, 'wphead');
        replaceField(task, 'wptxt');
        replaceField(task, 'after_answer');
        replaceField(task, 'password');
        if (task.raetsel) {
            replaceField(task.raetsel, 'raname');
            replaceField(task.raetsel, 'radescr');
            replaceField(task.raetsel, 'ratask');
            replaceField(task.raetsel, 'after_answer_comment_correct');
            replaceField(task.raetsel, 'after_answer_comment_wrong');
            replaceField(task.raetsel, 'js_task_settings');
            if (task.raetsel.hints) {
                Object.values(task.raetsel.hints).forEach((hint) => replaceField(hint, 'descr'));
            }
            replaceListField(task.raetsel, 'lsg');
            replaceListField(task.raetsel, 'lsg_correct');
        }
        return task;
    },
    updateSpotByCheckin(spot, timezone) {
        if (spot) {
            if (!(Vue._.isString(spot.checkin_time) && spot.checkin_time.trim() !== '')) {
                spot.checkin_time = methods.currentLocalTimeString(timezone);
            }
            let checkin_retries = spot.checkin_retries ? spot.checkin_retries + 1 : 1; // need this to trigger initWizard
            Vue.set(spot, 'checkin_retries', checkin_retries);
        }
        return spot;
    },
    updateSpotByCheckout(spot, timezone) {
        if (spot) {
            if (!(Vue._.isString(spot.checkout_time) && spot.checkout_time.trim() !== '')) {
                spot.checkout_time = methods.currentLocalTimeString(timezone);
            }
        }
        return spot;
    },
    updateSpotBySolveAction(spot, attrs, timezone, checkoutTime) {
        if (spot && attrs) {
            spot.tstatus = attrs.status;
            spot.trials = attrs.tries;
            // the moment sent to the server, so the local value matches the next refresh
            spot.checkout_time = hasDateValue(checkoutTime) ? checkoutTime : methods.currentLocalTimeString(timezone);
        }
        return spot;
    },
    // after a local checkout the countdown starts from the N-th checkout like in the native
    // app; the server value replaces it with the next event refresh
    syncFirstTaskCheckout(commit, state, rootState, eventId) {
        const event = rootState && rootState.events ? rootState.events.selected_event : null;
        if (Vue._.isEmpty(event) || !hasCountdown(event) || hasDateValue(event.first_task_checkout_time)) {
            return;
        }
        const value = nthCheckoutTime(state.list, event.countdown_task, (text) => methods.parseEventTime(text, event.timezone));
        if (value) {
            commit('events/SET_FIRST_TASK_CHECKOUT_TIME', { eventId: eventId, value: value }, { root: true });
        }
    },
    updateSpotByHintAction(spot, hintId, timezone) {
        if (spot && spot.raetsel && spot.raetsel.hints && spot.raetsel.hints[hintId]) {
            // Update the hint with timestamp to mark it as unlocked
            spot.raetsel.hints[hintId].timestamp = methods.currentLocalTimeString(timezone);
        }
        return spot;
    },
    currentLocalTimeString(timezone) {
        return moment.tz(new Date(), timezone).format();
    },
    // naive server date time in the event timezone or a local ISO string -> ms since epoch, NaN without a date value
    parseEventTime(value, timezone) {
        if (!hasDateValue(value)) {
            return NaN;
        }
        const formats = ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', moment.ISO_8601];
        const parsed = timezone ? moment.tz(String(value), formats, timezone) : moment(String(value), formats);
        return parsed.isValid() ? parsed.valueOf() : NaN;
    },
    getCurrentDateTimeWithOffset(offsetInS, timezone) {
        let date = new Date(new Date().getTime() + offsetInS * 1000);
        return moment.tz(date, timezone).format();
    },
    catchErrorCantPlayAgain(ex, errorClb) {
        if (isCantPlayAgainError(ex) && Vue._.isFunction(errorClb)) {
            errorClb({ error: 'cant_play_again' });
        }
    },
    sortList(list, order = { order: 'asc', option: 'byNumber' }) {
        return Vue._.orderBy(
            list,
            [
                (item) => {
                    let sortBy;
                    if (order.option === 'byPoints') {
                        sortBy = parseInt(item.points);
                    } else if (order.option === 'byName') {
                        sortBy = item.raetsel ? item.raetsel.raname : item.wphead;
                    } else {
                        //sort by task number (byNumber)
                        sortBy = parseInt(item.waintnr);
                    }
                    return item ? sortBy : 0;
                },
            ],
            [order.order],
        );
    },
    calculatePoints(taskList) {
        let points = 0;
        if (Vue._.isArray(taskList) && taskList.length > 0) {
            taskList.forEach((task) => {
                if (!Vue._.isEmpty(task) && task.tstatus === CONFIG.SPOT_STATE_SOLVED) {
                    points += task.points;

                    // Add hint points for unlocked hints
                    if (task.raetsel && task.raetsel.hints) {
                        // console.log("hints", task.raetsel.hints)
                        Object.values(task.raetsel.hints).forEach(hint => {
                            if (hint.timestamp && hint.points) {
                                points += hint.points
                            }
                        })
                    }
                }
            });
        }
        return points;
    },

    setDistanceToCurrentPosition(list, currentPosition){
        if( typeof currentPosition === 'object' && currentPosition !== null && typeof currentPosition.lat !== 'undefined' && typeof currentPosition.lng !== 'undefined' ){
            list.map( s =>
                {
                    Vue.set(s, "distanceToCurrentPosition", this.getDistanceToCurrentPosition(s, currentPosition));
                    return s;
                }
            )
        }
        else{
            list.map( s =>
                {
                    Vue.set(s, 'distanceToCurrentPosition', null);
                    return s;
                }
            )
        }
        return list;
    },
    getDistanceToCurrentPosition(spot, currentPosition){
        let distanceToCurrentPosition = null;
        if(spot.map_lat && spot.map_lon){
            distanceToCurrentPosition = GeoLocation.getDistance(
                {lat:spot.map_lat, lng:spot.map_lon},
                {lat:currentPosition.lat, lng:currentPosition.lng}
            );
        }
        return distanceToCurrentPosition;
    },
    /** Event flags of the auto-checkin rules; false without a selected event or a missing field. */
    autoCheckinFlags(rootState) {
        const event =
            rootState && rootState.events && !Vue._.isEmpty(rootState.events.selected_event)
                ? rootState.events.selected_event
                : null;
        return {
            autoCheckinOn: !!(event && event.auto_checkin),
            gpsDisabled: !!(event && event.disable_gps),
        };
    },
};

const routing = {
    filterSecretSpots(list) {
        return Vue._.filter(list, (l) => {
            return l && l.typ !== CONFIG.SPOT_IMG_TYPE_SECRET;
        });
    },

    updateNextTaskAfterSolving(state, task, updateSpotClb) {
        if (task && state) {
            let nextAvailableId = task.waallow;
            // only update if this is a different spot
            if (nextAvailableId > 0 && nextAvailableId !== task.id) {
                let nextTask = Utils.getBy('id', state.list, nextAvailableId);

                // Utils.getBy returns undefined on a miss (dangling pointer: spot was
                // deleted on the server) - a strict null check would crash below
                if (nextTask && nextTask.group === task.group) {
                    nextTask.isInGame = true; //TODO wird das benötigt?
                    if (nextTask.tstatus === CONFIG.SPOT_STATE_INACTIVE) {
                        // update the next spot to be playable
                        nextTask.tstatus = CONFIG.SPOT_STATE_ACTIVE;
                    }
                    if (Vue._.isFunction(updateSpotClb)) {
                        updateSpotClb(nextTask);
                    }
                }
            }
        }
    },
    updateAndFilterAllTasks(state) {
        Vue.set(state, 'list_active_invalid', true);
    },

    updateTaskAfterSolving(state, task, updateNextSpotClb) {
        if (task.tdose === CONFIG.ROUTING_TYPE_SINGLE || task.tdose === CONFIG.ROUTING_TYPE_MILESTONE) {
            if (task.waallow < 0) {
                // this is a milestone, update full list
                routing.updateAndFilterAllTasks(state);
            }
        } else if (task.tdose === CONFIG.ROUTING_TYPE_MULTI) {
            // this is a multi, update next if not the same id as current one
            if (task.tstatus === CONFIG.SPOT_STATE_SOLVED) {
                routing.updateNextTaskAfterSolving(state, task, updateNextSpotClb);
            }
        } else if (
            task.tdose === CONFIG.ROUTING_TYPE_ROUTE ||
            task.tdose === CONFIG.ROUTING_TYPE_HIDDENROUTE
        ) {
            // this is a route, update next if not the same id as current one
            if (task.tstatus === CONFIG.SPOT_STATE_SOLVED || task.tstatus === CONFIG.SPOT_STATE_FAILED) {
                routing.updateNextTaskAfterSolving(state, task, updateNextSpotClb);
            }
        }

        return task;
    },

    /** Auto-checkin spots whose checkin may fire now (in range or range-free), see utils/auto-checkin.mjs. */
    filterAutoCheckinSpots(rootState, list) {
        const flags = methods.autoCheckinFlags(rootState);
        return Vue._.filter(list, (spot) => canAutoCheckinNow(spot, flags));
    },

    /**
     * Algorithm to filter tasks
     */
    filterList(taskList, hideOnSolveExemptId = null) {
        // order by number for filtering
        taskList = methods.sortList(taskList);

        let hideWaypoint = false;
        let greyWaypoint = false;
        let milestoneFound = false;

        if (!Vue._.isArray(taskList) || taskList.length < 1) {
            return taskList;
        }

        taskList.forEach((task) => {
            if (!Vue._.isEmpty(task) && task.id) {
                if (milestoneFound) {
                    task.isInGame = false;
                } else {
                    if (
                        task.tdose === CONFIG.ROUTING_TYPE_SINGLE ||
                        task.tdose === CONFIG.ROUTING_TYPE_MILESTONE
                    ) {
                        task.isInGame = true;
                        // check for milestone
                        if (
                            task.tstatus === CONFIG.SPOT_STATE_INACTIVE ||
                            task.tstatus === CONFIG.SPOT_STATE_ACTIVE
                        ) {
                            if (task.waallow < 0) {
                                // TODO use milestone also in multi and hidden route?
                                //special case for milestone after route, set milestone inactive
                                if (greyWaypoint && task.tstatus !== CONFIG.SPOT_STATE_SOLVED) {
                                    task.tstatus = CONFIG.SPOT_STATE_INACTIVE;
                                }
                                milestoneFound = true;
                            }
                        }

                        // reset route and multi
                        hideWaypoint = false;
                        greyWaypoint = false;
                    } else if (task.tdose === CONFIG.ROUTING_TYPE_MULTI) {
                        if (hideWaypoint) {
                            task.isInGame = false;
                        } else {
                            task.isInGame = true;
                            if (greyWaypoint && task.tstatus !== CONFIG.SPOT_STATE_SOLVED) {
                                // if last spot was a route then set this as inactive
                                task.tstatus = CONFIG.SPOT_STATE_INACTIVE;
                            } else if (task.tstatus === CONFIG.SPOT_STATE_INACTIVE) {
                                // if spot is inactive because of route, set to active
                                task.tstatus = CONFIG.SPOT_STATE_ACTIVE;
                            }
                            // hide all tasks until reaching Single spot
                            if (task.tstatus !== CONFIG.SPOT_STATE_SOLVED) {
                                hideWaypoint = true;
                            }
                        }
                    } else if (task.tdose === CONFIG.ROUTING_TYPE_ROUTE) {
                        task.isInGame = true;
                        // set all tasks inactive until reaching Single spot (ignore spots that are already played)
                        if (
                            greyWaypoint &&
                            task.tstatus !== CONFIG.SPOT_STATE_SOLVED &&
                            task.tstatus !== CONFIG.SPOT_STATE_FAILED
                        ) {
                            task.tstatus = CONFIG.SPOT_STATE_INACTIVE;
                        } else if (task.tstatus === CONFIG.SPOT_STATE_INACTIVE) {
                            // if this spot in a route was inactive, set it active now
                            task.tstatus = CONFIG.SPOT_STATE_ACTIVE;
                        }
                        if (hideWaypoint) {
                            // if route comes after a multi, hide this spot
                            task.isInGame = false;
                        }

                        greyWaypoint =
                            task.tstatus !== CONFIG.SPOT_STATE_SOLVED &&
                            task.tstatus !== CONFIG.SPOT_STATE_FAILED;
                    } else if (task.tdose === CONFIG.ROUTING_TYPE_HIDDENROUTE) {
                        if (hideWaypoint) {
                            task.isInGame = false;
                        } else {
                            task.isInGame = true;

                            if (
                                greyWaypoint &&
                                task.tstatus !== CONFIG.SPOT_STATE_SOLVED &&
                                task.tstatus !== CONFIG.SPOT_STATE_FAILED
                            ) {
                                // if last spot was a route then set this as inactive
                                task.tstatus = CONFIG.SPOT_STATE_INACTIVE;
                            } else if (task.tstatus === CONFIG.SPOT_STATE_INACTIVE) {
                                // if spot is inactive because of route, set to active
                                task.tstatus = CONFIG.SPOT_STATE_ACTIVE;
                            }
                            // hide all tasks until reaching Single spot
                            if (
                                task.tstatus !== CONFIG.SPOT_STATE_SOLVED &&
                                task.tstatus !== CONFIG.SPOT_STATE_FAILED
                            ) {
                                hideWaypoint = true;
                            }
                        }
                    }
                }

                // hide_on_solve: a played spot leaves the game, except the one whose result
                // the player is still looking at (exempt until the detail is left)
                if (
                    task.hide_on_solve &&
                    task.id !== hideOnSolveExemptId &&
                    (task.tstatus === CONFIG.SPOT_STATE_SOLVED || task.tstatus === CONFIG.SPOT_STATE_FAILED)
                ) {
                    task.isInGame = false;
                }
            }
        });

        // return to previous sort order
        taskList = methods.sortList(taskList, state.sort_order);

        return Vue._.filter(taskList, (task) => {
            return !Vue._.isEmpty(task) && task.id;
        });
    },
    evaluateActions(state, rootState, groups, actions = [], currentPoints = 0) {
        let event =
            rootState && rootState.events && !Vue._.isEmpty(rootState.events.selected_event)
                ? rootState.events.selected_event
                : null;
        let timezone;
        let currentTimestamp;
        let startTimestamp;
        let parse = () => NaN;

        if (!Vue._.isEmpty(event)) {
            timezone = event.timezone;
            parse = (value) => methods.parseEventTime(value, timezone);
            currentTimestamp = Date.parse(methods.getCurrentDateTimeWithOffset(0, timezone));
            // countdown events run from the N-th task checkout (or now), the others from the event start
            startTimestamp = hasCountdown(event)
                ? countdownStartMs(event, currentTimestamp, parse)
                : parse(event.evdatum + ' ' + event.evstart);
        }

        actions.forEach((action) => {
            let group = Utils.findGroupByGroupId(groups, action.group_id);
            if (group) {
                if (action.visible !== group.isActive) {
                    let task = Utils.findTaskInGroupsByTaskId(groups, action.eventwaypoint_id);
                    switch (action.trigger_id) {
                        case 1: // EventTimeReached
                            if (action.time * 1000 <= currentTimestamp - startTimestamp) {
                                group.isActive = action.visible === 1;
                            }
                            break;
                        case 2: // EventPointsReached
                            if (action.points <= currentPoints) {
                                group.isActive = action.visible === 1;
                            }
                            break;
                        case 3: // TaskSolved
                            if (task && task.tstatus === 3) {
                                group.isActive = action.visible === 1;
                            }
                            break;
                        case 4: // TaskFailed
                            if (task && task.tstatus === 4) {
                                group.isActive = action.visible === 1;
                            }
                            break;
                        case 5: // McSolutionSelected
                            if (
                                task &&
                                (task.tstatus === 3 || task.tstatus === 4) &&
                                task.raetsel &&
                                task.raetsel.user_input
                            ) {
                                task.raetsel.user_input.forEach((input) => {
                                    if (input === action.solution) {
                                        group.isActive = action.visible === 1;
                                    }
                                });
                            }
                            break;
                        case 6: // TaskTimeSinceSolvingReached
                            if (task && (task.tstatus === 3 || task.tstatus === 4)) {
                                const checkoutTimestamp = parse(task.checkout_time);
                                if (isFinite(checkoutTimestamp) && checkoutTimestamp + action.time * 1000 <= currentTimestamp) {
                                    group.isActive = action.visible === 1;
                                }
                            }
                            break;
                    }
                }
            }
        });

        this.evaluateActiveGroupsForFallback(groups);
        this.evaluateActionTimers(state, groups, actions, currentTimestamp, startTimestamp, parse);
    },
    evaluateActiveGroupsForFallback(groups = []) {
        // filter groups with fallback and without active tasks
        let groupList = Vue._.filter(groups, (group) => {
            if (group && group.isActive && group.group_next_id !== null && group.group_next_id !== 0) {
                let activeTaskList = Vue._.filter(group.tasks, (task) => {
                    return task && task.tstatus === 1 && task.isInGame;
                });
                group.tasks.filter(this.filterActiveTask);
                return activeTaskList.length === 0;
            }
            return false;
        });

        // activate groups
        groupList.forEach((gr) => {
            let groupToActivate = Utils.findGroupByGroupId(groups, gr.group_next_id);
            if (groupToActivate) {
                groupToActivate.isActive = true;
            }
        });
    },
    filterActiveTask(task) {
        return task && task.tstatus === 1 && task.isInGame;
    },
    evaluateActionTimers(state, groups, actions, currentTimestamp, startTimestamp, parse) {
        let timerList = [];
        if (groups && actions && currentTimestamp && startTimestamp) {
            timerList.push.apply(
                timerList,
                this.evaluateEventTimeReachedTimer(actions, currentTimestamp, startTimestamp),
            );
            timerList.push.apply(timerList, this.evaluateTaskTimer(groups, actions, currentTimestamp, parse));
        }

        Vue.set(state, 'action_timers', timerList);
    },
    evaluateEventTimeReachedTimer(actions, currentTimestamp, startTimestamp) {
        let timerList = [];
        let actionsList = Vue._.filter(actions, (a) => {
            return a.trigger_id === 1;
        });

        actionsList.forEach((action) => {
            if (action) {
                let actionTimeStamp = startTimestamp + action.time * 1000;
                if (actionTimeStamp > currentTimestamp) {
                    timerList.push({ name: 'ACTION_' + action.id_group_action, stamp: actionTimeStamp });
                }
            }
        });
        return timerList;
    },
    evaluateTaskTimer(groups, actions, currentTimestamp, parse) {
        let timerList = [];
        let actionsList = Vue._.filter(actions, (a) => {
            return a.trigger_id === 6;
        });

        actionsList.forEach((action) => {
            if (action) {
                let task = Utils.findTaskInGroupsByTaskId(groups, action.eventwaypoint_id);
                if (task && (task.tstatus === 3 || task.tstatus === 4)) {
                    const checkoutTimestamp = parse(task.checkout_time);
                    let actionTimeStamp = checkoutTimestamp + action.time * 1000;
                    if (isFinite(checkoutTimestamp) && actionTimeStamp > currentTimestamp) {
                        timerList.push({ name: 'ACTION_' + action.id_group_action, stamp: actionTimeStamp });
                    }
                }
            }
        });
        return timerList;
    },
    getActiveTasksFromGroups(groups = []) {
        let taskList = [];
        groups.forEach((group) => {
            if (group && group.isActive) {
                let filtered = Vue._.filter(group.tasks, (task) => {
                    return !Vue._.isEmpty(task) && task.id && task.isInGame;
                });
                taskList.push.apply(taskList, filtered);
            }
        });
        return taskList;
    },
};

const state = {
    list: [],
    groups_routing: [],
    group_actions: [],
    action_timers: [],
    list_active_cached: [],
    list_active_autocheckin: [],
    list_active_invalid: true,
    list_loading: false,
    spot_loading: false,
    spot_solve_loading: false,
    list_error: false,
    api_loading: false,
    sort_order: { order: 'asc', option: 'byNumber' },
    hide_on_solve_exempt_id: null, // just played spot, stays visible until its detail is left
    // auto-checkin spots whose solve got no server answer; not reopened until the next successful
    // event refresh (event loop) or spot list load
    autocheckin_suppressed_ids: [],
    // spots found by QR scan or password this game session: their checkin step needs no scan
    verified_checkin_spot_ids: [],
    offlineStatistic: [],
    offlineStatistic_invalid: true,
    current_position: null,
    js_tasks: {},
};

// getters
const getters = {
    allSpots: (state) => {
        return state.list;
    },
    allActiveSpots: (state, getters, rootState) => {
        if (!state.list_active_invalid && !Vue._.isEmpty(state.list_active_cached)) {
            return methods.sortList(state.list_active_cached, state.sort_order);
        }

        let points = 0;
        let groups = state.groups_routing;
        groups.forEach((group) => {
            group.tasks = routing.filterList(group.tasks, state.hide_on_solve_exempt_id);
            points = points + methods.calculatePoints(group.tasks);
        });

        routing.evaluateActions(state, rootState, groups, state.group_actions, points);

        Vue.set(state, 'list_active_invalid', false);

        let cached_list = methods.sortList(routing.getActiveTasksFromGroups(groups), state.sort_order);

        cached_list = methods.setDistanceToCurrentPosition(cached_list, getters.currentPosition);
        let auto_list = routing.filterAutoCheckinSpots(rootState, cached_list);

        Vue.set(state, 'list_active_cached', cached_list);
        Vue.set(state, 'list_active_autocheckin', auto_list);

        return state.list_active_cached;
    },
    allActiveVisibleSpots: (state, getters) => {
        let allActiveVisibleSpots = routing.filterSecretSpots(getters.allActiveSpots);
        allActiveVisibleSpots = methods.setDistanceToCurrentPosition(allActiveVisibleSpots, getters.currentPosition);
        return allActiveVisibleSpots;
    },
    currentPosition: (state) => {
        return state.current_position;
    },
    allAutoCheckinSpots: (state) => {
        return state.list_active_autocheckin;
    },
    // classification alone, independent of distance and state (utils/auto-checkin.mjs)
    isAutoCheckinSpot: (state, getters, rootState) => (spot) => {
        return isAutoCheckinSpot(spot, methods.autoCheckinFlags(rootState));
    },
    autoCheckinSuppressedIds: (state) => {
        return state.autocheckin_suppressed_ids;
    },
    isCheckinVerified: (state) => (id) => {
        return state.verified_checkin_spot_ids.includes(id);
    },
    hasActiveSpots: (state, getters, rootState) => {
        this.allActiveSpots(state, getters, rootState).some((task) => {
            return (
                task &&
                (task.tstatus === CONFIG.SPOT_STATE_ACTIVE || task.tstatus === CONFIG.SPOT_STATE_INACTIVE)
            );
        });
    },
    getOfflineStatistic: (state, getters, rootState) => {
        // console.log("-------getOfflineStatistic-------")
        if (!state.offlineStatistic_invalid && !Vue._.isEmpty(state.offlineStatistic) && Object.keys(state.offlineStatistic).length) {
            return state.offlineStatistic;
        }
        let groups = state.groups_routing;
        let myStatistic = [];
        groups.forEach((group) => {
            if (group.tasks) {
                myStatistic = Utils.getOfflineStatistic(group.tasks, myStatistic)
            }
        });

        let event =
            rootState && rootState.events && !Vue._.isEmpty(rootState.events.selected_event)
                ? rootState.events.selected_event
                : null;


        if (!Vue._.isEmpty(event)) {
            myStatistic.teamId = event.team_id
            myStatistic.teamName = event.team_name;
            // myStatistic.userName = this.app_user_statistic.username;
            // myStatistic.teamIcon = this.app_team_photo
        }

        Vue.set(state, 'offlineStatistic', myStatistic);
        Vue.set(state, 'offlineStatistic_invalid', false);

        return state.offlineStatistic
    },
    hasQrCheckin: (state) => {
        return state.hasQrCheckin;
    },
    hasPwCheckin: (state) => {
        return state.hasPwCheckin;
    },

    jsTaskById: (state) => (id) => {
        return state.js_tasks[id].code || null;
    },

};

// actions
const actions = {
    getSpotsByEventId({ commit, rootState }, data) {
        let eventId = Vue._.isObject(data) ? data.eventId : data;
        let silent = Vue._.isObject(data) && data.silent;
        commit('SET_SPOTS_LOADING', !silent);
        return new Promise((resolve, reject) => {
            api
                .getSpotsByEventId(eventId)
                .then((response) => {
                    commit('SET_SPOT_LIST_INVALIDATE', true); //Trigger to render list of active spots again
                    if (response && response.data && response.data.eventwaypoints) {
                        // mainGroup for spots without a group; its follower
                        // (fallback once the start spots are played out) is a
                        // route field of the response, 0 = none
                        let mainGroup = {
                            id_group: 0,
                            name: 'mainGroup',
                            color: '',
                            group_next_id: Number(response.data.start_group_next_id) || 0,
                            group_parent_id: null,
                            is_final: false,
                            microevent_id: eventId,
                            isActive: true,
                        };
                        let groups = response.data.group;
                        // add isActive
                        groups.forEach((group) => {
                            group.isActive = false;
                        });
                        groups.push(mainGroup);
                        // custom keyword definitions of the selected event, extracted
                        // on event checkin (which resolves before this request starts)
                        const customKeywords =
                            rootState.events && rootState.events.custom_keywords
                                ? rootState.events.custom_keywords
                                : {};
                        let solutions = Vue._.map(response.data.solution, (sol) => ({
                            id_solution: sol.id_solution,
                            solution:
                                typeof sol.solution === 'string'
                                    ? replaceCustomKeywords(sol.solution, customKeywords)
                                    : sol.solution,
                        }));
                        // map solutions into actions
                        let actions = response.data.group_action;
                        actions.forEach((action) => {
                            if (action.solution_id !== null) {
                                let solution = solutions.find((item) => item.id_solution === action.solution_id);
                                action.solution = solution.solution;
                            }
                        });
                        let tasks = Vue._.map(response.data.eventwaypoints, (e) => {
                            return methods.applyCustomKeywordsToSpot(methods.mapSpot(e), customKeywords);
                        });
                        // map tasks into groups
                        let isHavingQr = false;
                        let isHavingPw = false;

                        tasks.forEach((task) => {
                            if (Object.prototype.hasOwnProperty.call(task, 'id')) {
                                let groupId = task.group;
                                if (!groupId) {
                                    task.group = 0;
                                    groupId = 0;
                                }
                                // let group = Utils.findGroupByGroupId(groups, groupId);
                                let group = groups.find(item => item.id_group === groupId);
                                if (!('tasks' in group)) {
                                    group.tasks = [];
                                }
                                group.tasks.push(task);
                                if (!isHavingQr && task.indoor === 2) {
                                    isHavingQr = true;
                                }
                                if (!isHavingPw && task.indoor === 5) {
                                    isHavingPw = true;
                                }
                            }
                        });

                        commit('SET_GROUPS', groups);
                        commit('SET_ACTIONS', actions);
                        commit('SET_SPOTS', tasks);
                        commit('SET_HAS_QR_CHECKIN', isHavingQr);
                        commit('SET_HAS_PW_CHECKIN', isHavingPw);
                        commit('SET_OFFLINE_STATISTIC_INVALIDATE', true);
                        commit('SET_AUTOCHECKIN_SUPPRESSED', []);
                        // commit('SET_OFFLINE_STATISTIC', myStatistic);
                        if(response.data.js_tasks){
                            commit('SET_JS_TASKS', response.data.js_tasks);
                        }
                        resolve(groups);
                    } else {
                        reject(null);
                    }
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    commit('SET_SPOTS_LOADING', false);
                });
        });
    },
    getSpotById({ state }, id) {
        return new Promise((resolve) => {
            let obj = Utils.getBy('id', state.list, id);
            resolve(obj);
        });
    },

    checkinSpot({ commit, dispatch }, data) {
        commit('SET_SINGLE_SPOT_LOADING', true);
        //Other logic: checkin by default locally and send lazy checkin request
        return new Promise((resolve, reject) => {
            let id = data ? data.id : null;
            let eventId = data ? data.eventId : null;
            let errorClb = data ? data.onError : null;
            let responseClb = data ? data.onResponse : null;
            let doNotSend = data ? data.doNotSend : null;
            let timezone = data ? data.timezone : null;

            dispatch('getSpotById', id)
                .then((spot) => {
                    spot = methods.updateSpotByCheckin(spot, timezone);
                    commit('ADD_SPOT', spot);
                    if (!doNotSend) {
                        api
                            .checkinSpotById(id, eventId, { date: spot.checkin_time })
                            .then(() => {
                                commit('SET_SINGLE_SPOT_LOADING', false); // TODO - just testing if this resolves white window error
                                if (Vue._.isFunction(responseClb)) {
                                    responseClb(spot);
                                }
                            })
                            .catch((ex) => {
                                commit('SET_SINGLE_SPOT_LOADING', false); // TODO - just testing if this resolves white window error
                                methods.catchErrorCantPlayAgain(ex, errorClb);
                            })
                            .finally(() => {
                                commit('SET_SINGLE_SPOT_LOADING', false);
                            });
                    }
                    commit('SET_SINGLE_SPOT_LOADING', false); // TODO - just testing if this resolves white window error
                    resolve(spot);
                })
                .catch((ex) => {
                    commit('SET_SINGLE_SPOT_LOADING', false); // TODO - just testing if this resolves white window error
                    reject(ex);
                })
                .finally(() => {
                    commit('SET_SINGLE_SPOT_LOADING', false);
                });
        });
    },

    /** Takes a local checkin back (no server call); list and statistic are re-evaluated. */
    resetCheckinSpot({ commit, dispatch }, id) {
        commit('SET_SINGLE_SPOT_LOADING', true);
        return new Promise((resolve, reject) => {
            dispatch('getSpotById', id)
                .then((spot) => {
                    spot.checkin_time = null;
                    commit('ADD_SPOT', spot);
                    commit('SET_SPOT_LIST_INVALIDATE', true);
                    commit('SET_OFFLINE_STATISTIC_INVALIDATE', true);
                    resolve(spot);
                })
                .catch(() => {
                    reject(null);
                })
                .finally(() => {
                    commit('SET_SINGLE_SPOT_LOADING', false);
                });
        });
    },
    suppressAutoCheckin({ commit }, id) {
        commit('ADD_AUTOCHECKIN_SUPPRESSED', id);
    },
    releaseAutoCheckin({ commit }) {
        commit('SET_AUTOCHECKIN_SUPPRESSED', []);
    },
    solveSpot({ commit, dispatch, state, rootState }, data) {
        return new Promise((resolve, reject) => {
            let id = data ? data.id : null;
            let eventId = data ? data.eventId : null;
            let serverData = data ? data.serverData : {};
            let userInputs = serverData ? serverData['input_json'] : {};
            let errorClb = data ? data.onError : null;
            let successClb = data ? data.onSuccess : null;
            let timezone = data ? data.timezone : null;

            //Set offline checkout time
            serverData.date = serverData.date
                ? serverData.date
                : methods.getCurrentDateTimeWithOffset(-1, timezone); // -1 to prevent incorrect solution after image/video upload in a time based quest

            commit('SET_SPOT_SOLVE_LOADING', true);

            api
                .solveSpotById(id, eventId, serverData)
                .then((response) => {
                    if (Vue._.isFunction(successClb)) {
                        successClb();
                    }

                    if (response) {
                        if (response.message === 'cant_play_again') {
                            reject(response.message);
                        } else {
                            dispatch('getSpotById', id)
                                .then((spot) => {
                                    let updatedSpot = methods.updateSpotBySolveAction(spot, response.data, timezone, serverData.date);

                                    //Add user inputs locally
                                    if (updatedSpot.raetsel && userInputs) {
                                        //if we have a js task
                                        if (methods.isSolutionTypeJsTask(updatedSpot)){
                                            //we will build the message as server response
                                            updatedSpot.raetsel.user_input = [JSON.stringify(userInputs.userInput)];
                                        }else{
                                            updatedSpot.raetsel.user_input = Vue._.map(userInputs, (i, e) => {
                                                return e;
                                            });
                                        }

                                        updatedSpot.raetsel.user_input_json = userInputs;
                                    }
                                    //Routing
                                    routing.updateTaskAfterSolving(state, updatedSpot, (nextSpot) => {
                                        //Update next spot
                                        commit('ADD_SPOT', nextSpot);
                                    });
                                    commit('ADD_SPOT', updatedSpot);
                                    methods.syncFirstTaskCheckout(commit, state, rootState, eventId);
                                    commit('SET_HIDE_ON_SOLVE_EXEMPT', updatedSpot.id); // result stays visible until the player leaves the spot
                                    commit('SET_SPOT_LIST_INVALIDATE', true); //Trigger to render list of active spots again
                                    commit('SET_OFFLINE_STATISTIC_INVALIDATE', true); //Trigger to render list of active spots again
                                    resolve(updatedSpot);
                                })
                                .catch((error) => {
                                    console.error(error);
                                    reject(null);
                                });
                        }
                    } else {
                        reject(null);
                    }
                })
                .catch((ex) => {
                    methods.catchErrorCantPlayAgain(ex, errorClb);
                    reject(ex);
                })
                .finally(() => {
                    commit('SET_SPOT_SOLVE_LOADING', false);
                });
        });
    },
    requestHint({ commit, dispatch }, data) {
        return new Promise((resolve, reject) => {
            let hintId = data ? data.hintId : null;
            let wpId = data ? data.wpId : null;
            let eventId = data ? data.eventId : null;
            let serverData = data ? data.serverData : {};
            let errorClb = data ? data.onError : null;
            let successClb = data ? data.onSuccess : null;
            let timezone = data ? data.timezone : null;

            //Set offline checkout time
            serverData.date = serverData.date
                ? serverData.date
                : methods.getCurrentDateTimeWithOffset(-1, timezone); // -1 to prevent incorrect solution after image/video upload in a time based quest

            commit('SET_HINT_REQUEST_LOADING', true);

            api
                .requestHintById(hintId, wpId, eventId, serverData)
                .then((response) => {
                    if (Vue._.isFunction(successClb)) {
                        successClb();
                    }

                    if (response) {
                        if (response.message === 'cant_play_again') {
                            reject(response.message);
                        } else {
                            dispatch('getSpotById', wpId)
                                .then((spot) => {
                                    // Update hint data in spot
                                    let updatedSpot = methods.updateSpotByHintAction(spot, hintId, timezone);
                                    
                                    // Commit the updated spot to the store
                                    commit('ADD_SPOT', updatedSpot);
                                    commit('SET_SPOT_LIST_INVALIDATE', true); //Trigger to render list of active spots again
                                    commit('SET_OFFLINE_STATISTIC_INVALIDATE', true); //Trigger to render list of active spots again
                                    resolve(updatedSpot);
                                })
                                .catch((error) => {
                                    console.error(error);
                                    reject(null);
                                });
                        }
                    } else {
                        reject(null);
                    }
                })
                .catch((ex) => {
                    methods.catchErrorCantPlayAgain(ex, errorClb);
                    reject(ex);
                })
                .finally(() => {
                    commit('SET_HINT_REQUEST_LOADING', false);
                });
        });
    },
    refreshSpots({ commit }) {
        commit('SET_SPOT_LIST_INVALIDATE', true);
    },
    /**
     * The player left the detail of the given spot. Drops its hide_on_solve exemption and
     * re-filters, so a played spot with hide_on_solve disappears from list and map now.
     */
    leaveSpot({ commit, state }, spotId) {
        if (spotId && spotId === state.hide_on_solve_exempt_id) {
            commit('SET_HIDE_ON_SOLVE_EXEMPT', null);
            commit('SET_SPOT_LIST_INVALIDATE', true);
        }
    },
    setCurrentPosition({ commit, dispatch }, position) {
        commit('SET_CURRENT_POSITION', position);
        dispatch('updateLists');
    },

    updateLists({ state, getters, rootState }){
        let cached_list = methods.setDistanceToCurrentPosition(state.list_active_cached, getters.currentPosition);
        let auto_list = routing.filterAutoCheckinSpots(rootState, cached_list);
        Vue.set(state, 'list_active_cached', cached_list);
        Vue.set(state, 'list_active_autocheckin', auto_list);

    },


    destroy({ commit }) {
        commit('SET_GROUPS', []);
        commit('SET_ACTIONS', []);
        commit('SET_ACTIONS_TIMER', []);
        commit('SET_SPOTS', []);
        commit('SET_CACHED_SPOTS', []);
        commit('SET_AUTOCHECKIN_SPOTS', []);
        commit('SET_JS_TASKS', {});
        commit('SET_HIDE_ON_SOLVE_EXEMPT', null);
        commit('SET_AUTOCHECKIN_SUPPRESSED', []);
        commit('SET_CHECKIN_VERIFIED', []);
    },
};

// mutations
const mutations = {
    SET_GROUPS(state, groups_routing) {
        Vue.set(state, 'groups_routing', groups_routing);
    },
    SET_ACTIONS(state, group_actions) {
        Vue.set(state, 'group_actions', group_actions);
    },
    SET_ACTIONS_TIMER(state, action_timers) {
        Vue.set(state, 'action_timers', action_timers);
    },
    SET_SPOTS(state, list) {
        let newEntries = Vue._.isArray(list) ? list : [];
        Vue.set(state, 'list', newEntries);
    },
    SET_CACHED_SPOTS(state, list) {
        Vue.set(state, 'list_active_cached', list);
    },
    SET_AUTOCHECKIN_SPOTS(state, list) {
        Vue.set(state, 'list_active_autocheckin', list);
    },
    SET_AUTOCHECKIN_SUPPRESSED(state, ids) {
        Vue.set(state, 'autocheckin_suppressed_ids', Vue._.isArray(ids) ? ids : []);
    },
    ADD_AUTOCHECKIN_SUPPRESSED(state, id) {
        if (id && !state.autocheckin_suppressed_ids.includes(id)) {
            state.autocheckin_suppressed_ids.push(id);
        }
    },
    SET_CHECKIN_VERIFIED(state, ids) {
        Vue.set(state, 'verified_checkin_spot_ids', Vue._.isArray(ids) ? ids : []);
    },
    ADD_CHECKIN_VERIFIED(state, id) {
        if (id && !state.verified_checkin_spot_ids.includes(id)) {
            state.verified_checkin_spot_ids.push(id);
        }
    },
    SET_HAS_QR_CHECKIN(state, hasQr) {
        Vue.set(state, 'hasQrCheckin', hasQr);
    },
    SET_HAS_PW_CHECKIN(state, hasPw) {
        Vue.set(state, 'hasPwCheckin', hasPw);
    },
    SET_CURRENT_POSITION(state, position) {
        Vue.set(state, 'current_position', position);
    },
    ADD_SPOT(state, item) {
        if (!Vue._.isEmpty(item)) {
            let groups = state.groups_routing;
            let group = Utils.findGroupByGroupId(groups, item.group);
            if (group) {
                let index = Utils.getIndexById(group.tasks, item.id);
                if (index > -1) {
                    group.tasks[index] = item;
                } else {
                    group.tasks.push(item);
                }
                Vue.set(state, 'groups_routing', groups);
            }
        }
    },
    SET_SPOTS_LOADING(state, val) {
        Vue.set(state, 'list_loading', val);
    },
    SET_SINGLE_SPOT_LOADING(state, val) {
        Vue.set(state, 'spot_loading', val);
    },
    SET_SPOT_SOLVE_LOADING(state, val) {
        Vue.set(state, 'spot_solve_loading', val);
    },
    SET_HINT_REQUEST_LOADING(state, val) {
        Vue.set(state, 'hint_request_loading', val);
    },
    SET_SPOTS_ERROR(state, val) {
        Vue.set(state, 'list_error', val);
    },
    SET_API_LOADING(state, val) {
        Vue.set(state, 'api_loading', val);
    },
    SET_SORT_ORDER(state, val) {
        Vue.set(state, 'sort_order', val);
    },
    SET_SPOT_LIST_INVALIDATE(state, val) {
        Vue.set(state, 'list_active_invalid', val);
    },
    SET_HIDE_ON_SOLVE_EXEMPT(state, spotId) {
        Vue.set(state, 'hide_on_solve_exempt_id', spotId);
    },
    SET_OFFLINE_STATISTIC_INVALIDATE(state, val) {
        Vue.set(state, 'offlineStatistic_invalid', val);
    },
    SET_JS_TASKS(state, js_tasks) {
        // console.log('js_tasks: ', js_tasks);
        Vue.set(state, 'js_tasks', js_tasks);
    },

};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
